import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  InputAdornment,
  FormHelperText,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward,
  ArrowBack as ArrowBackIcon,
  CheckCircle,
  Language as LanguageIcon,
  ExpandMore,
  ExpandLess,
  CloudUpload,
  InfoOutlined,
  EditOutlined,
  AccessTime as AccessTimeIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  VerifiedUser as VerifiedUserIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneIphone as MobileIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

/* ----------------------------------------------------------------------
   Shared building blocks for the Create/Edit Application wizard.
   Both CreateApplicationPage and EditApplicationPage render the SAME
   step components (via ApplicationWizard) so edit looks and behaves
   exactly like create, just pre-filled and hitting update endpoints.
---------------------------------------------------------------------- */

/* ----------------------------------------------------------------------
   Design tokens
---------------------------------------------------------------------- */
// Design tokens for the wizard, dark/light aware. This is deliberately a
// plain function (not a hook) — many of the components below are
// implicit-return arrows (`() => (<JSX/>)`), so calling getC() straight
// inside their JSX is far simpler than converting every one of them to a
// block body just to call useTheme(). It re-derives on every render from
// the `data-theme-mode` attribute ThemeContext.jsx already keeps in sync
// on <body>, so it still reacts correctly to a live theme toggle (the
// ThemeProvider re-render already cascades down to these components).
const isDarkMode = () =>
  typeof document !== "undefined" && document.body?.dataset?.themeMode === "dark";

export const getC = () => {
  const isDark = isDarkMode();
  return {
    border: isDark ? "rgba(255,255,255,0.14)" : "#e2e8f0",
    blue: "#3182ce",
    blueDark: "#2b6cb0",
    green: "#38a169",
    greenDark: "#276749",
    indigo: "#4c51bf",
    amber: "#dd6b20",
    amberBg: isDark ? "rgba(221,107,32,0.22)" : "#feebc8",
    amberText: isDark ? "#fbd38d" : "#9c4221",
    textPrimary: isDark ? "#f8fafc" : "#1a202c",
    textSecondary: isDark ? "#cbd5e1" : "#718096",
    bgLight: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
    badgeActiveBg: isDark ? "rgba(56,161,105,0.22)" : "#c6f6d5",
    badgeActiveText: isDark ? "#68d391" : "#276749",
    badgeInactiveBg: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
    badgeInactiveText: isDark ? "#cbd5e1" : "#4a5568",
    badgeBlockedBg: isDark ? "rgba(229,62,62,0.22)" : "#fed7d7",
    badgeBlockedText: isDark ? "#fc8181" : "#9b2c2c",
    infoBg: isDark ? "rgba(49,130,206,0.18)" : "#ebf8ff",
    infoBorder: isDark ? "rgba(49,130,206,0.4)" : "#bee3f8",
    infoText: isDark ? "#90cdf4" : "#2c5282",
  };
};

/* ----------------------------------------------------------------------
   Validation schemas
---------------------------------------------------------------------- */
export const detailsSchema = Yup.object({
  businessId: Yup.string().required("Business is required"),
  applicationType: Yup.string()
    .oneOf(["WEB", "MOBILE"])
    .required("Application type is required"),
  businessName: Yup.string().required("Display name is required").max(100),
  description: Yup.string().max(500),
  isActive: Yup.boolean(),
  appLogoUrl: Yup.string().url("Invalid URL").nullable(),
});

// Mirrors the backend's own check (businessApp domainValidation.js —
// isValidDomainName) so a malformed domain gets caught here, inline, instead
// of only failing later when the row/app is actually saved.
const DOMAIN_NAME_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;

export const domainSchema = Yup.object({
  domain: Yup.string()
    .required("Domain is required")
    .test(
      "valid-domain",
      "Enter a valid domain, e.g. app.example.com",
      (val) => DOMAIN_NAME_REGEX.test((val || "").trim())
    ),
  displayName: Yup.string().required("Display Name is required"),
  redirectUrl: Yup.string()
    .required("Redirect URL is required")
    .test("has-path", "Redirect URL is required", (val) => Boolean(getPath(val).trim())),
  logoUrl: Yup.string().url("Enter a valid URL").required("Logo URL is required"),
});

export const settingsSchema = Yup.object({
  blockDuration: Yup.number()
    .min(1, "Min 1 minute")
    .max(1440, "Max 1440 minutes")
    .required("Required"),
  failedAttempts: Yup.number()
    .min(1, "Min 1")
    .max(99, "Max 99")
    .required("Required"),
  // IP Allowlisting — disabled for now, commented out along with its UI.
  // ipGeolocationEnabled: Yup.boolean(),
  webhookUrl: Yup.string().url("Invalid URL").required("Webhook URL is required"),
  metadataJson: Yup.string()
    .required("Metadata is empty. This field is required.")
    .test("not-blank", "Metadata is empty. This field is required.", (val) => !!val && val.trim().length > 0)
    .test("is-json", "Metadata must be valid JSON", (val) => {
      if (!val || !val.trim()) return true;
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    })
    .test("non-empty-object", "Metadata is empty. Provide at least one key.", (val) => {
      if (!val || !val.trim()) return true;
      try {
        const o = JSON.parse(val);
        return !(o && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length === 0);
      } catch {
        return true;
      }
    }),
});

/* Step definitions. The "domains" step is filtered out entirely for
   MOBILE applications. */
export const ALL_STEPS = [
  {
    key: "details",
    label: "Application Details",
    subtitle: "Basic information",
  },
  {
    key: "domains",
    label: "Domains & URLs",
    subtitle: "Configure domains and URLs",
  },
  {
    key: "settings",
    label: "Security & Settings",
    subtitle: "Security and access control",
  },
  {
    key: "review",
    label: "Review & Create",
    subtitle: "Review and create application",
  },
];

export const BLOCK_DURATION_OPTIONS = [5, 15, 30, 60, 120, 240, 1440];
export const FAILED_ATTEMPTS_OPTIONS = [3, 5, 10, 15, 20, 25];

export const emptyDomain = () => ({
  id: null,
  // The real API has no domain-id — a domain is addressed by its
  // domainName in the URL (PUT/DELETE .../domains/:domainName/:appId).
  // We track the name it was LAST SAVED under separately from the
  // (possibly edited, not-yet-saved) `domain` field below.
  savedDomainName: null,
  domain: "",
  displayName: "",
  redirectUrl: "https://",
  status: true,
  saved: false,
  logoUrl: "", // only URL, no radio selection
  _tempId: Date.now() + Math.random(),
  isDirty: false,
});

export const getProtocol = (url = "") =>
  url.startsWith("http://") ? "http" : "https";
export const getPath = (url = "") => url.replace(/^https?:\/\//, "");

// A domain row only becomes a real domain once it has a name — an empty
// "New Domain" row the user added but never filled in (or gave up on)
// shouldn't be counted anywhere as a configured domain. Matches the same
// filter ApplicationWizard.jsx's handleCreate uses to build the actual
// create-application payload, so what's shown here always matches what
// actually gets submitted.
export const isValidDomainRow = (d) => Boolean(d?.domain && d.domain.trim());

/* ----------------------------------------------------------------------
   Section Card – the shared "domain card" visual language: a bordered,
   rounded container with a light-grey header (icon avatar + title +
   optional badge/action) and top-labelled fields in the body. Reused
   across every step so the whole wizard reads as one consistent system.
---------------------------------------------------------------------- */
export const SectionCard = ({ icon, title, badge, action, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${getC().border}`,
      borderRadius: 2,
      overflow: "hidden",
      mb: 2,
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.5,
        bgcolor: getC().bgLight,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
      >
        {icon && (
          <Avatar
            variant="rounded"
            sx={{
              width: 36,
              height: 36,
              bgcolor: getC().indigo,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Typography variant="body1" fontWeight={700} noWrap>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {badge}
        {action}
      </Box>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// A business can only get new applications while it is ACTIVE and not blocked.
export const isBusinessInactive = (biz) =>
  String(biz?.status || biz?.accountStatus || "ACTIVE").toUpperCase() === "INACTIVE";
export const isBusinessLocked = (biz) => Boolean(biz?.isBlocked) || isBusinessInactive(biz);

/* Field label used above every input inside a SectionCard, matching the
   domain fields' top-label style. */
export const FieldLabel = ({ children }) => (
  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
    {children}
  </Typography>
);

/* ----------------------------------------------------------------------
   Status badge
---------------------------------------------------------------------- */
export const StatusBadge = ({ active, blocked = false }) => (
  <Chip
    label={blocked ? "BLOCKED" : active ? "ACTIVE" : "INACTIVE"}
    size="small"
    sx={{
      fontWeight: 700,
      fontSize: 11,
      height: 22,
      bgcolor: blocked ? getC().badgeBlockedBg : active ? getC().badgeActiveBg : getC().badgeInactiveBg,
      color: blocked ? getC().badgeBlockedText : active ? getC().badgeActiveText : getC().badgeInactiveText,
    }}
  />
);

/* ----------------------------------------------------------------------
   Saved / unsaved badge (used in the domains list)
---------------------------------------------------------------------- */
export const SaveStateBadge = ({ saved, dirty }) => {
  let label = saved ? 'Saved' : 'Unsaved';
  let bgcolor = saved ? getC().badgeActiveBg : getC().amberBg;
  let color = saved ? getC().badgeActiveText : getC().amberText;

  // If saved but dirty, show "Unsaved" (pending changes)
  if (saved && dirty) {
    label = 'Unsaved';
    bgcolor = getC().amberBg;
    color = getC().amberText;
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor, color }}
    />
  );
};
/* ----------------------------------------------------------------------
   Inline toggle row
---------------------------------------------------------------------- */
export const InlineToggleRow = ({ label, description, checked, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
    }}
  >
    <Typography variant="body2" noWrap sx={{ overflow: "visible" }}>
      <Box component="span" fontWeight={600}>
        {label}
      </Box>
      {description && (
        <Box component="span" sx={{ color: getC().textSecondary, ml: 1 }}>
          — {description}
        </Box>
      )}
    </Typography>
    <Switch checked={checked} onChange={onChange} color="primary" />
  </Box>
);

/* ----------------------------------------------------------------------
   Step 1 — Application Details
   `lockBusiness` / `lockType` disable those two fields in edit mode —
   changing which business an app belongs to, or flipping WEB↔MOBILE
   after domains/settings already exist for it, is not something the
   backend endpoints here are built to handle safely.
---------------------------------------------------------------------- */
export const Step1Details = ({
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  errors,
  touched,
  businesses,
  isAdmin,
  fetching,
  lockBusiness = false,
  lockType = false,
  businessInactive = false,
}) => {
  const selectedBusiness =
    businesses.find((b) => b._id === values.businessId) || null;
  const typeIcon =
    values.applicationType === "MOBILE" ? (
      <MobileIcon fontSize="small" />
    ) : (
      <LanguageIcon fontSize="small" />
    );

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Application Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Provide basic information about your application
      </Typography>

      <SectionCard
        icon={typeIcon}
        title={values.businessName || "New Application"}
        badge={<StatusBadge active={values.isActive} />}
        sx={{ mb: 0 }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {isAdmin && (
            <Box>
              <FieldLabel>Select Business *</FieldLabel>
              <Autocomplete
                options={businesses}
                loading={fetching}
                value={selectedBusiness}
                disabled={lockBusiness}
                getOptionLabel={(biz) => biz.businessName || ""}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                // A blocked or INACTIVE business can't have new applications
                // created for it (enforced server-side too, in
                // assertBusinessExists) — it still shows up in the list so an
                // admin can see it, but it can't be selected.
                getOptionDisabled={(biz) => isBusinessLocked(biz)}
                onChange={(_, val) => {
                  if (val && isBusinessLocked(val)) return;
                  setFieldValue("businessId", val ? val._id : "");
                }}
                onBlur={() => setFieldTouched && setFieldTouched("businessId", true)}
                renderOption={(props, biz) => (
                  <Box
                    component="li"
                    {...props}
                    key={biz._id}
                    sx={{
                      justifyContent: "space-between !important",
                      opacity: isBusinessLocked(biz) ? 0.5 : 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {biz.businessName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {biz.isBlocked
                          ? "Blocked — cannot create applications for this business"
                          : isBusinessInactive(biz)
                            ? "Inactive — activate the business to create applications"
                            : biz._id}
                      </Typography>
                    </Box>
                    <StatusBadge
                      blocked={Boolean(biz.isBlocked)}
                      active={!biz.isBlocked && (biz.status || biz.accountStatus || "ACTIVE").toUpperCase() === "ACTIVE"}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select a business"
                    size="medium"
                    error={touched.businessId && Boolean(errors.businessId)}
                    helperText={
                      (touched.businessId && errors.businessId) ||
                      (lockBusiness
                        ? "Business can't be changed after the app is created."
                        : undefined)
                    }
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: getC().textPrimary,
                        color: getC().textPrimary,
                      },
                      "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: getC().border,
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          <Box>
            <FieldLabel>Application Name *</FieldLabel>
            <TextField
              fullWidth
              size="medium"
              name="businessName"
              placeholder="Enter Application name to display"
              value={values.businessName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.businessName && Boolean(errors.businessName)}
              helperText={touched.businessName && errors.businessName}
            />
          </Box>

          <Box>
            <FieldLabel>Application Type *</FieldLabel>
            <FormControl
              fullWidth
              size="medium"
              error={touched.applicationType && Boolean(errors.applicationType)}
            >
              <Select
                name="applicationType"
                displayEmpty
                value={values.applicationType}
                disabled={lockType}
                sx={{
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: getC().textPrimary,
                    color: getC().textPrimary,
                  },
                  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                    borderColor: getC().border,
                  },
                }}
                onBlur={handleBlur}
                onChange={(e) => {
                  const type = e.target.value;
                  setFieldValue("applicationType", type);
                  if (type === "MOBILE") {
                    setFieldValue("domains", []);
                  } else if (values.domains.length === 0) {
                    setFieldValue("domains", [emptyDomain()]);
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select application type
                </MenuItem>
                <MenuItem value="WEB">Web Application</MenuItem>
                <MenuItem value="MOBILE">Mobile Application</MenuItem>
              </Select>
              {touched.applicationType && errors.applicationType ? (
                <FormHelperText error>{errors.applicationType}</FormHelperText>
              ) : lockType ? (
                <FormHelperText>
                  Type can't be changed after the app is created.
                </FormHelperText>
              ) : null}
            </FormControl>
          </Box>

          <Box>
            <FieldLabel>Description (optional)</FieldLabel>
            <TextField
              fullWidth
              size="medium"
              multiline
              minRows={2}
              name="description"
              placeholder="Enter application description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <FormControlLabel
          control={
            <Switch
              checked={values.isActive}
              onChange={(e) => setFieldValue("isActive", e.target.checked)}
              disabled={businessInactive}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Enable Application
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {businessInactive
                  ? "This business is inactive — its applications stay disabled until it is activated"
                  : "Application will be active after creation"}
              </Typography>
            </Box>
          }
        />
      </SectionCard>
    </Box>
  );
};

/* ----------------------------------------------------------------------
   Preview sidebar (wider, tighter internal spacing)
---------------------------------------------------------------------- */
const NEXT_STEP_COPY = {
  details:
    "Configure domains, URLs, and redirect settings for your application.",
  domains:
    "Set up block duration, failed-attempt limits, and other security settings.",
  settings: "Review everything and save your application.",
  review: "You’re all set — hit the button below to finish up.",
};

export const PreviewSidebar = ({ values, showDomains, currentStepKey }) => {
  const nextStepText =
    currentStepKey === "details" && !showDomains
      ? "Configure security and access-control settings for your application."
      : NEXT_STEP_COPY[currentStepKey] || NEXT_STEP_COPY.details;

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, border: `1px solid ${getC().border}`, p: 3 }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Application Preview
      </Typography>
      <Box
        sx={{
          border: `1px solid ${getC().border}`,
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          mb: 2.5,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          {values.businessName || "Application Name"}
        </Typography>
        {values.applicationType && (
          <Chip
            label={
              values.applicationType === "WEB"
                ? "Web Application"
                : "Mobile Application"
            }
            size="medium"
            sx={{
              bgcolor: getC().badgeActiveBg,
              color: getC().badgeActiveText,
              fontWeight: 600,
              my: 1,
            }}
          />
        )}
        <Typography variant="body2" color="text.secondary">
          {values.businessId || "Business Name"}
        </Typography>
      </Box>

      <Typography
        variant="subtitle2"
        fontWeight={700}
        color={getC().blue}
        sx={{ mb: 1.5 }}
      >
        Overview
      </Typography>
      <Box sx={{ border: `1px solid ${getC().border}`, borderRadius: 2, p: 2 }}>
        {[
          ["Business", values.businessId || "Not selected"],
          [
            "Type",
            values.applicationType === "WEB"
              ? "Web Application"
              : values.applicationType === "MOBILE"
                ? "Mobile Application"
                : "-",
          ],
          ["Status", values.isActive ? "Active" : "Inactive"],
          [
            "Domains",
            showDomains
              ? (() => {
                  const count = (values.domains || []).filter(isValidDomainRow).length;
                  return count ? `${count} configured` : "Not configured";
                })()
              : "N/A",
          ],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{ display: "flex", justifyContent: "space-between", py: 0.6 }}
          >
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: 2.5,
          bgcolor: getC().infoBg,
          border: `1px solid ${getC().infoBorder}`,
          borderRadius: 2,
          p: 2,
          display: "flex",
          gap: 1.5,
        }}
      >
        <InfoOutlined sx={{ color: getC().infoText, fontSize: 20, mt: 0.2 }} />
        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: getC().infoText }}
          >
            {currentStepKey === "review" ? "Ready to Save" : "Next Step"}
          </Typography>
          <Typography variant="caption" sx={{ color: getC().infoText }}>
            {nextStepText}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/* ----------------------------------------------------------------------
   Step 2 — Domains & URLs
---------------------------------------------------------------------- */
export const Step2Domains = ({
  values,
  savingDomain,
  onSaveDomain,
  onRemoveDomain,
  onToggleDomain,
  updateDomainField,
  addDomainRow,
  expandedDomains,
  setExpandedDomains,
  appLogoUrl,
  domainErrors = {},
  domainTouched = {},
  onFieldBlur,
}) => {
  const domains = values.domains;

  const toggleExpand = (index) =>
    setExpandedDomains((prev) => ({ ...prev, [index]: !prev[index] }));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Domains & URLs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add every domain that's allowed to access this application, along
            with where it should redirect after authentication.
          </Typography>
        </Box>
        <Chip
          label={(() => {
            const count = domains.filter(isValidDomainRow).length;
            return `${count} ${count === 1 ? "domain" : "domains"}`;
          })()}
          sx={{
            bgcolor: getC().bgLight,
            color: getC().textPrimary,
            fontWeight: 700,
            border: `1px solid ${getC().border}`,
          }}
        />
      </Box>

      {domains.map((domain, index) => {
        const expanded = expandedDomains[index] ?? true;
        const domainLogoSource = domain.logoUrl || appLogoUrl || null;
        const rowErrors = domainErrors[index] || {};
        const rowTouched = domainTouched[index] || {};

        return (
          <Box
            key={domain._tempId || index}
            sx={{
              border: `1px solid ${domain.saved ? getC().border : getC().amber}44`,
              borderRadius: 2,
              mb: 2,
              overflow: "hidden",
              transition: "border-color 0.15s ease",
            }}
          >
            {/* Card header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.5,
                bgcolor: getC().bgLight,
                cursor: "pointer",
              }}
              onClick={() => toggleExpand(index)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: domain.domain ? getC().indigo : getC().bgLight,
                    color: domain.domain ? "#fff" : getC().textSecondary,
                    border: domain.domain ? "none" : `1px dashed ${getC().border}`,
                  }}
                >
                  <LanguageIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={700} noWrap>
                    {domain.domain || "New Domain"}
                  </Typography>
                  {!expanded && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {domain.redirectUrl && getPath(domain.redirectUrl)
                        ? domain.redirectUrl
                        : "No redirect URL set"}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                onClick={(e) => e.stopPropagation()}
              >
                <StatusBadge active={domain.status} />
                <SaveStateBadge saved={domain.saved} dirty={domain.isDirty} />
                <Button
                  size="small"
                  onClick={() => onRemoveDomain(index)}
                  disabled={savingDomain}
                  sx={{ color: "#e53e3e", minWidth: "auto" }}
                >
                  Remove
                </Button>
                <IconButton size="small" onClick={() => toggleExpand(index)}>
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Box>

            {expanded && (
              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Domain *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="example.com"
                      value={domain.domain}
                      onChange={(e) =>
                        updateDomainField(index, "domain", e.target.value)
                      }
                      onBlur={() => onFieldBlur && onFieldBlur(index, "domain")}
                      size="medium"
                      error={Boolean(rowTouched.domain && rowErrors.domain)}
                      helperText={rowTouched.domain && rowErrors.domain}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Display Name *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Marketing Site"
                      value={domain.displayName}
                      onChange={(e) =>
                        updateDomainField(index, "displayName", e.target.value)
                      }
                      onBlur={() => onFieldBlur && onFieldBlur(index, "displayName")}
                      size="medium"
                      error={Boolean(rowTouched.displayName && rowErrors.displayName)}
                      helperText={rowTouched.displayName && rowErrors.displayName}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Redirect URL *
                    </Typography>
                    <TextField
                      fullWidth
                      value={getPath(domain.redirectUrl)}
                      onChange={(e) =>
                        updateDomainField(
                          index,
                          "redirectUrl",
                          `${getProtocol(domain.redirectUrl)}://${e.target.value}`,
                        )
                      }
                      onBlur={() => onFieldBlur && onFieldBlur(index, "redirectUrl")}
                      size="medium"
                      error={Boolean(rowTouched.redirectUrl && rowErrors.redirectUrl)}
                      helperText={rowTouched.redirectUrl && rowErrors.redirectUrl}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Select
                              variant="standard"
                              disableUnderline
                              value={getProtocol(domain.redirectUrl)}
                              onChange={(e) =>
                                updateDomainField(
                                  index,
                                  "redirectUrl",
                                  `${e.target.value}://${getPath(domain.redirectUrl)}`,
                                )
                              }
                              sx={{ mr: 1 }}
                            >
                              <MenuItem value="https">https</MenuItem>
                              {/* <MenuItem value="http">http</MenuItem> */}
                            </Select>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Logo URL *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="https://example.com/domain-logo.png"
                      value={domain.logoUrl}
                      onChange={(e) =>
                        updateDomainField(index, "logoUrl", e.target.value)
                      }
                      onBlur={() => onFieldBlur && onFieldBlur(index, "logoUrl")}
                      size="medium"
                      error={Boolean(rowTouched.logoUrl && rowErrors.logoUrl)}
                      helperText={
                        (rowTouched.logoUrl && rowErrors.logoUrl) ||
                        "Logo shown for this domain."
                      }
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        border: `1px solid ${getC().border}`,
                        borderRadius: 2,
                        width: 96,
                        height: 96,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: getC().bgLight,
                        overflow: "hidden",
                        p: 1,
                      }}
                    >
                      {domainLogoSource ? (
                        <Box
                          component="img"
                          src={domainLogoSource}
                          alt="logo preview"
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No logo
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={domain.status}
                        onChange={() => onToggleDomain(index)}
                      />
                    }
                    label={domain.status ? "Active" : "Inactive"}
                  />
                  {domain.saved && !domain.isDirty ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: getC().badgeActiveText,
                      }}
                    >
                      <CheckCircle fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>
                        Saved — edit any field to make changes
                      </Typography>
                    </Box>
                  ) : (
                    <Button
                      size="medium"
                      variant="contained"
                      onClick={() => onSaveDomain(index)}
                      disabled={savingDomain}
                      sx={{ background: getC().blue, px: 4 }}
                    >
                      {savingDomain ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Save Domain"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}

      {/* Single, clear entry point for adding another domain */}
      <Box
        onClick={addDomainRow}
        sx={{
          border: `1.5px dashed ${getC().border}`,
          borderRadius: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: "pointer",
          color: getC().blue,
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          "&:hover": { bgcolor: getC().infoBg, borderColor: getC().blue },
        }}
      >
        <AddIcon fontSize="small" />
        <Typography variant="body2" fontWeight={700}>
          Add another domain
        </Typography>
      </Box>
    </Box>
  );
};

/* ----------------------------------------------------------------------
   Step 3 — Security & Settings
---------------------------------------------------------------------- */
export const Step3Security = ({
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  businessInactive = false,
}) => (
  <Box>
    <Typography variant="h6" fontWeight={700} gutterBottom>
      Security & Settings
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Configure security options and additional settings for this application.
    </Typography>

    <SectionCard icon={<LockIcon fontSize="small" />} title="Security Settings">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <FieldLabel>Block Duration *</FieldLabel>
          <FormControl
            fullWidth
            size="medium"
            error={touched.blockDuration && Boolean(errors.blockDuration)}
          >
            <Select
              name="blockDuration"
              value={values.blockDuration}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {BLOCK_DURATION_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {v} minutes
                </MenuItem>
              ))}
            </Select>
            {touched.blockDuration && errors.blockDuration ? (
              <FormHelperText error>{errors.blockDuration}</FormHelperText>
            ) : (
              <FormHelperText>
                Duration for blocking after suspicious activity.
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box>
          <FieldLabel>Failed Attempts *</FieldLabel>
          <FormControl
            fullWidth
            size="medium"
            error={touched.failedAttempts && Boolean(errors.failedAttempts)}
          >
            <Select
              name="failedAttempts"
              value={values.failedAttempts}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {FAILED_ATTEMPTS_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {v} attempts
                </MenuItem>
              ))}
            </Select>
            {touched.failedAttempts && errors.failedAttempts ? (
              <FormHelperText error>{errors.failedAttempts}</FormHelperText>
            ) : (
              <FormHelperText>Failed attempts before blocking.</FormHelperText>
            )}
          </FormControl>
        </Box>
      </Box>

      {/* IP Allowlisting — disabled for now
      <Divider sx={{ my: 2.5 }} />

      <FormControlLabel
        control={
          <Switch
            checked={values.ipGeolocationEnabled}
            onChange={(e) =>
              setFieldValue("ipGeolocationEnabled", e.target.checked)
            }
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={600}>
              IP Allowlisting
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Restrict access to specific IP addresses
            </Typography>
          </Box>
        }
      />
      */}
    </SectionCard>

    <SectionCard
      icon={<SettingsIcon fontSize="small" />}
      title="Advanced Settings"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <FieldLabel>Webhook URL *</FieldLabel>
          <TextField
            fullWidth
            size="medium"
            name="webhookUrl"
            placeholder="https://example.com/webhook/auth-events"
            value={values.webhookUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.webhookUrl && Boolean(errors.webhookUrl)}
            helperText={
              (touched.webhookUrl && errors.webhookUrl) ||
              "Receive real-time notifications and events."
            }
          />
        </Box>

        <Box>
          <FieldLabel>Metadata (JSON)</FieldLabel>
          <TextField
            fullWidth
            size="medium"
            multiline
            minRows={3}
            name="metadataJson"
            value={values.metadataJson}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={'{\n  "publicKey": "./keys/merchant_public.pem"\n}'}
            error={Boolean(errors.metadataJson)}
            helperText={errors.metadataJson}
            InputProps={{
              sx: {
                fontFamily: "Menlo, Consolas, monospace",
                fontSize: 13,
                bgcolor: getC().bgLight,
              },
            }}
          />
        </Box>
      </Box>
    </SectionCard>

    <SectionCard
      icon={<PersonOutlineIcon fontSize="small" />}
      title="Application Status"
      badge={<StatusBadge active={values.isActive} />}
      sx={{ mb: 0 }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={values.isActive}
            onChange={(e) => setFieldValue("isActive", e.target.checked)}
            disabled={businessInactive}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Enable Application
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {businessInactive
                ? "This business is inactive — its applications stay disabled until it is activated"
                : "Application will be active after creation"}
            </Typography>
          </Box>
        }
      />
    </SectionCard>
  </Box>
);

/* ----------------------------------------------------------------------
   Step 4 — Review & Save
   No API call — the app, its domains, and its settings were already
   saved step-by-step. This is purely a read-only confirmation screen.
---------------------------------------------------------------------- */
export const SummarySection = ({ title, onEdit, children, sx }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.5,
      borderColor: getC().border,
      borderRadius: 2,
      height: "100%",
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {onEdit && (
        <Button
          size="small"
          startIcon={<EditOutlined fontSize="small" />}
          onClick={onEdit}
          sx={{ color: getC().blue }}
        >
          Edit
        </Button>
      )}
    </Box>
    {children}
  </Paper>
);

export const StatTile = ({ icon, label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderColor: getC().border,
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        bgcolor: getC().infoBg,
        color: getC().infoText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={700} noWrap>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export const Step4Review = ({
  values,
  setActiveStep,
  visibleSteps,
  createdByEmail,
  mode = "create",
}) => {
  const showDomains = values.applicationType === "WEB";
  const stepIndex = (key) => visibleSteps.findIndex((s) => s.key === key);
  const metadataCount = (() => {
    try {
      return Object.keys(JSON.parse(values.metadataJson || "{}")).length;
    } catch {
      return 0;
    }
  })();

  const detailRow = (label, value) => (
    <Grid item xs={12} sm={6} key={label}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {mode === "edit" ? "Review Changes" : "Review & Create"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {mode === "edit"
          ? "Everything below was already saved as you moved through each step."
          : "Confirm all details before creating the application."}
      </Typography>

      {/* Hero summary */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderColor: getC().border,
          borderRadius: 3,
          bgcolor: getC().bgLight,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: getC().indigo,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              fontWeight: 700,
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            {values.appLogoUrl ? (
              <Box
                component="img"
                src={values.appLogoUrl}
                alt="logo"
                sx={{ width: "100%", height: "100%", objectFit: "contain", bgcolor: "#fff", p: 0.75 }}
              />
            ) : (
              (values.businessName || "AP").slice(0, 2).toUpperCase()
            )}
          </Box>
          <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} noWrap>
              {values.businessName || "Application"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={
                  values.applicationType === "WEB"
                    ? "Web Application"
                    : "Mobile Application"
                }
                size="small"
                sx={{
                  bgcolor: getC().bgLight,
                  color: getC().textPrimary,
                  fontWeight: 600,
                  border: `1px solid ${getC().border}`,
                }}
              />
              <StatusBadge active={values.isActive} />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: getC().textSecondary,
            }}
          >
            <PersonOutlineIcon fontSize="small" />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {mode === "edit" ? "Last edited by" : "Created by"}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {createdByEmail}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* At-a-glance stat tiles */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={<PublicIcon fontSize="small" />}
            label="Domains"
            value={showDomains ? values.domains.filter(isValidDomainRow).length : "N/A"}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={<AccessTimeIcon fontSize="small" />}
            label="Block Duration"
            value={`${values.blockDuration} min`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={<LockIcon fontSize="small" />}
            label="Failed Attempts"
            value={`${values.failedAttempts}`}
          />
        </Grid>
        {/* IP Allowlisting — disabled for now
        <Grid item xs={6} md={3}>
          <StatTile
            icon={<VerifiedUserIcon fontSize="small" />}
            label="IP Allowlisting"
            value={values.ipGeolocationEnabled ? "Enabled" : "Disabled"}
          />
        </Grid>
        */}
      </Grid>

      {/* Two-up detail sections */}
      <Grid container spacing={3} sx={{ mb: showDomains ? 3 : 0 }}>
        <Grid item xs={12} md={6}>
          <SummarySection
            title="Application Details"
            onEdit={() => setActiveStep(stepIndex("details"))}
          >
            <Grid container spacing={2}>
              {detailRow("Application Name", values.businessName)}
              {detailRow(
                "Application Type",
                values.applicationType === "WEB"
                  ? "Web Application"
                  : "Mobile Application",
              )}
              {detailRow("Business", values.businessId || "N/A")}
              {detailRow("Description", values.description || "Not provided")}
            </Grid>
          </SummarySection>
        </Grid>

        <Grid item xs={12} md={6}>
          <SummarySection
            title="Security & Settings"
            onEdit={() => setActiveStep(stepIndex("settings"))}
          >
            <Grid container spacing={2}>
              {detailRow("Block Duration", `${values.blockDuration} minutes`)}
              {detailRow(
                "Failed Attempts Before Block",
                `${values.failedAttempts} attempts`,
              )}
              {/* IP Allowlisting — disabled for now
              {detailRow(
                "IP Allowlisting",
                values.ipGeolocationEnabled ? "Enabled" : "Disabled",
              )}
              */}
              {detailRow("Webhook URL", values.webhookUrl || "Not configured")}
              {detailRow(
                "Metadata",
                metadataCount > 0 ? `${metadataCount} keys configured` : "None",
              )}
            </Grid>
          </SummarySection>
        </Grid>
      </Grid>

      {showDomains && (
        <SummarySection
          title="Domains & URLs"
          onEdit={() => setActiveStep(stepIndex("domains"))}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Redirect URL</TableCell>
                  <TableCell>Logo</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.domains.filter(isValidDomainRow).map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{d.domain}</TableCell>
                    <TableCell>{d.displayName || "-"}</TableCell>
                    <TableCell>{d.redirectUrl}</TableCell>
                    <TableCell>{d.logoUrl ? "✓" : "-"}</TableCell>
                    <TableCell>
                      <StatusBadge active={d.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SummarySection>
      )}
    </Box>
  );
};
